import Tag, { TagVariant } from '~/shared/components/Tag';
import Tooltip from '~/shared/components/Tooltip';
import Typography from '~/shared/components/Typography';

interface RenderTagGroupOptions<T> {
  items: T[] | undefined;
  getText: (item: T) => string;
  getUniqueKey: (item: T, index: number) => string;
  variant: TagVariant;
  maxVisible: number;
  tooltipIdPrefix: string;
  size?: 'sm' | 'md';
}

export default function RenderTagGroup<T>({
  items,
  getText,
  getUniqueKey,
  variant,
  maxVisible,
  tooltipIdPrefix,
  size = 'sm',
}: RenderTagGroupOptions<T>): JSX.Element {
  if (!items || items.length === 0) {
    return <></>;
  }

  const elements: JSX.Element[] = [];

  // Map variant to tooltip label
  const getTooltipLabel = (variant: TagVariant): string => {
    switch (variant) {
      case 'node':
        return 'Node Group:';
      case 'instance':
        return 'Instances:';
      case 'cluster':
        return 'Cluster:';
      case 'volume':
        return 'Tag:';
      default:
        return `${variant}:`;
    }
  };

  const visibleItems = items.slice(0, maxVisible);
  const hiddenItems = items.slice(maxVisible);

  // Add tooltips to all visible tags
  visibleItems.forEach((item, index) => {
    const tagText = getText(item);
    const uniqueTooltipId = `${tooltipIdPrefix}-tag-${index}`;

    elements.push(
      <span key={`${tooltipIdPrefix}-tag-wrapper-${index}`} className="flex items-center">
        <div
          data-tooltip-id={uniqueTooltipId}
          data-tooltip-content={tagText}
          className="inline-block overflow-visible"
        >
          <Tag
            key={getUniqueKey(item, index)}
            text={tagText}
            variant={variant}
            size={size}
            className="cursor-pointer"
          />
        </div>
        <Tooltip id={uniqueTooltipId} place="top" />
      </span>
    );
  });

  if (hiddenItems.length > 0) {
    const uniqueTooltipId = `${tooltipIdPrefix}-plus-tooltip`;

    elements.push(
      <span key={`${tooltipIdPrefix}-plus-wrapper`} className="flex items-center overflow-visible">
        <div data-tooltip-id={uniqueTooltipId} className="inline-block overflow-visible">
          <Tag
            key={`${tooltipIdPrefix}-plus`}
            text={`+${hiddenItems.length}`}
            variant={variant}
            size={size}
            className="cursor-pointer"
          />
        </div>
        <Tooltip
          id={uniqueTooltipId}
          place="top"
          render={() => (
            <div className="flex flex-col gap-1 text-white">
              <Typography variant="p2" className="font-500 text-white">
                {getTooltipLabel(variant)}
              </Typography>
              {hiddenItems.map((item, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <div className="size-2 aspect-square bg-white rounded-full mb-auto mt-2" />
                  <Typography variant="p3" className="font-normal  text-white">
                    {getText(item)}
                  </Typography>
                </div>
              ))}
            </div>
          )}
        />
      </span>
    );
  }

  return <>{elements}</>;
}
